/**
 * mediaEmbed.js — Helpers pour l'intégration de médias externes (YouTube, etc.)
 *
 * Sécurité : ne JAMAIS injecter une URL non validée dans un src d'iframe.
 * Utiliser isYouTube(url) avant d'appeler getYouTubeEmbedUrl(url).
 */

/**
 * Extrait l'ID d'une vidéo YouTube depuis diverses formes d'URL.
 * Supporte :
 *  - https://www.youtube.com/watch?v=ID
 *  - https://youtu.be/ID
 *  - https://www.youtube.com/shorts/ID
 *  - https://www.youtube.com/embed/ID
 *  - https://m.youtube.com/watch?v=ID
 * @returns {string|null} L'ID YouTube (11 chars typiques), sinon null.
 */
export function getYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\.|^m\./, '');

    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0];
      return _validId(id);
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      // /watch?v=ID
      const v = u.searchParams.get('v');
      if (v) return _validId(v);
      // /shorts/ID, /embed/ID, /v/ID
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length >= 2 && ['shorts', 'embed', 'v'].includes(parts[0])) {
        return _validId(parts[1]);
      }
    }
    return null;
  } catch {
    // Pas une URL valide
    return null;
  }
}

function _validId(id) {
  if (!id) return null;
  // Un ID YouTube est composé de 11 caractères [A-Za-z0-9_-]
  const clean = String(id).replace(/[^A-Za-z0-9_-]/g, '');
  return /^[A-Za-z0-9_-]{6,20}$/.test(clean) ? clean : null;
}

/**
 * Vrai si l'URL correspond à une vidéo YouTube parseable.
 */
export function isYouTube(url) {
  return !!getYouTubeId(url);
}

/**
 * URL d'embed YouTube (via youtube-nocookie.com pour RGPD).
 * @param {string} url URL YouTube d'origine
 * @param {object} opts { autoplay: boolean }
 */
export function getYouTubeEmbedUrl(url, opts = {}) {
  const id = getYouTubeId(url);
  if (!id) return null;
  const params = new URLSearchParams();
  if (opts.autoplay) params.set('autoplay', '1');
  params.set('rel', '0'); // pas de suggestions à la fin
  const qs = params.toString();
  return `https://www.youtube-nocookie.com/embed/${id}${qs ? '?' + qs : ''}`;
}

/**
 * URL de la miniature YouTube (façade, pas de cookies YT tant que non chargée).
 * hqdefault est disponible pour toutes les vidéos ; maxresdefault peut manquer.
 */
export function getYouTubeThumbnailUrl(url, quality = 'hqdefault') {
  const id = getYouTubeId(url);
  if (!id) return null;
  const q = ['default', 'mqdefault', 'hqdefault', 'sddefault', 'maxresdefault'].includes(quality)
    ? quality
    : 'hqdefault';
  return `https://img.youtube.com/vi/${id}/${q}.jpg`;
}
